import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useAppStateStore = defineStore('app-state', () => {
    const isOnline = ref(navigator.onLine);
    // Foreground/visibility gate for the offline banner (Angular AppStateService.isVisible).
    const isVisible = ref(!document.hidden);
    const viewportWidth = ref(window.innerWidth);
    const isNative = !!window.kgrCapacitor;

    /**
     * Angular `AppStateService.isInIframe` (`app-state.service.ts:34,49-55`): `!isNative && inIframe()`,
     * wobei `inIframe()` den Vergleich in try/catch kapselt und im Fehlerfall `true` liefert — im
     * Zweifel also "eingebettet". Relevant fuer club-offering, das per iFrame in fremde
     * Vereins-Websites eingebunden wird (APP_MIGRATE.md §3); ein ungefangener Fehler traefe dort
     * den App-Bootstrap. Einmal berechnet wie in Angular, nicht reaktiv.
     */
    const isInIframe
        = !isNative
            && (() => {
                try {
                    return window.self !== window.top;
                } catch {
                    return true;
                }
            })();

    const isScreenSmallAndDown = computed(() => viewportWidth.value < 768);
    const isScreenMediumAndUp = computed(() => viewportWidth.value >= 768);
    const isScreenLargeAndUp = computed(() => viewportWidth.value >= 1024);

    window.addEventListener('online', () => (isOnline.value = true));
    window.addEventListener('offline', () => (isOnline.value = false));
    window.addEventListener('resize', () => (viewportWidth.value = window.innerWidth));
    document.addEventListener('visibilitychange', () => (isVisible.value = !document.hidden));

    // Native (Capacitor): `visibilitychange` is unreliable in the WebView, so mirror Angular
    // AppStateService and drive visibility from the App plugin lifecycle. Re-read the online state
    // on resume (Angular re-checks navigator.onLine when the app becomes active again).
    if (isNative) {
        void import('@capacitor/app').then(({ App }) => {
            App.addListener('appStateChange', ({ isActive }) => {
                isVisible.value = isActive;
                if (isActive) {
                    isOnline.value = navigator.onLine;
                }
            });
        });
    }

    // Angular `AppStateService.onTerminateCallbacks` (app-state.service.ts:28/102/107): work that has
    // to finish before the WebView is torn down. The list starts with `App.removeAllListeners()`;
    // the Capacitor push handler appends `PushNotifications.removeAllListeners()`. Only `reload()`
    // runs them — dropping the native listeners first keeps the old WebView from handling events
    // that belong to the reloaded one.
    const onTerminateCallbacks: (() => Promise<unknown>)[] = [];
    if (isNative) {
        onTerminateCallbacks.push(async () => {
            const { App } = await import('@capacitor/app');
            return App.removeAllListeners();
        });
    }

    function addOnTerminateCallback(callback: () => Promise<unknown>) {
        onTerminateCallbacks.push(callback);
    }

    async function reload() {
        await Promise.allSettled(onTerminateCallbacks.map((callback) => callback()));
        window.location.reload();
    }

    return {
        isOnline,
        isVisible,
        isNative,
        isInIframe,
        isScreenSmallAndDown,
        isScreenMediumAndUp,
        isScreenLargeAndUp,
        addOnTerminateCallback,
        reload,
    };
});
